import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { CustomsQueueRow } from '@crm/shared'
import { acceptPreviousCustomsAnswers } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { confirm } from '../../../shared/ui/confirmStore'
import { toast } from '../../../shared/ui/toastStore'
import { exportToBroker } from '../broker/brokerActions'
import { BrokerUploadButton } from '../broker/BrokerUploadButton'
import { BROKER_TOO_MANY_TEXT, CUSTOMS_BULK_MAX, setCustomsStatusInChunks } from '../customsBulk'
import { countUniqueParts, pluralPositions } from '../customsSelection'
import { SendToBrokerModal } from './SendToBrokerModal'

const BTN =
  'px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
const BTN_PRIMARY =
  'px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

/** Итог массового действия для тоста: «Готово: 5 позиций; пропущено…». */
export function bulkResultText(prefix: string, r: { updated: number; skipped: number }): string {
  const skipped = r.skipped
    ? `; пропущено (уже не в очереди нетарифки): ${pluralPositions(r.skipped)}`
    : ''
  return `${prefix}: ${pluralPositions(r.updated)}${skipped}`
}

interface Props {
  selected: CustomsQueueRow[]
  onAnswer: () => void
  onNeedsInfo: () => void
  /** После любого действия: сбросить выделение в гриде. */
  onDone: () => void
}

/**
 * Действия логиста над выделенными строками очереди: ответ вручную, прошлый
 * ответ по партномеру («Оставить прошлый ответ»), статусы
 * «Нужна информация» / «Не требуется» / «Вернуть в очередь», файл брокеру и
 * загрузка ответа брокера. Показывается только логисту.
 */
export function CustomsActionsBar({ selected, onAnswer, onNeedsInfo, onDone }: Props) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [sending, setSending] = useState(false)
  const n = selected.length
  const uniqueParts = countUniqueParts(selected)
  const none = n === 0 || busy
  // Позиции без ответа, по партномеру которых есть прошлая проверка.
  const withPrevious = selected.filter((r) => r.hint && r.status !== 'ANSWERED')

  const refresh = () => void qc.invalidateQueries({ queryKey: ['customs'] })

  const setStatus = async (status: 'PENDING' | 'NOT_REQUIRED') => {
    const title =
      status === 'NOT_REQUIRED'
        ? `Отметить «Не требуется»: ${pluralPositions(n)}?`
        : `Вернуть в очередь: ${pluralPositions(n)}?`
    const message =
      status === 'NOT_REQUIRED'
        ? 'Проверка у брокера по этим позициям не нужна — они уйдут во вкладку «Обработано».'
        : 'Позиции снова получат статус «В очереди»; связь с запросом брокеру снимется.'
    if (!(await confirm({ title, message, confirmText: 'Да' }))) return
    setBusy(true)
    try {
      // Больше 2000 позиций сервер за раз не примет — уходит порциями.
      const res = await setCustomsStatusInChunks(
        selected.map((r) => r.itemId),
        status,
      )
      toast.success(
        bulkResultText(status === 'NOT_REQUIRED' ? '«Не требуется»' : 'Возвращено в очередь', res),
      )
      onDone()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось сменить статус'))
    } finally {
      setBusy(false)
      refresh()
    }
  }

  const keepPrevious = async () => {
    const m = withPrevious.length
    const ok = await confirm({
      title: `Оставить прошлый ответ: ${pluralPositions(m)}?`,
      message:
        'Ответ прошлой проверки по партномеру (ТН ВЭД, пошлина, сбор, «Честный знак», нетарифка) станет ответом по позиции, брокера заново не спрашиваем. Позиции уйдут во вкладку «Обработано», менеджеры получат уведомление.' +
        (m < n ? ` Остальные ${pluralPositions(n - m)} без прошлого ответа не изменятся.` : ''),
      confirmText: 'Оставить',
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await acceptPreviousCustomsAnswers(withPrevious.map((r) => r.itemId))
      toast.success(bulkResultText('Оставлен прошлый ответ', res))
      onDone()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось оставить прошлый ответ'))
    } finally {
      setBusy(false)
      refresh()
    }
  }

  const openSend = () => {
    // Один запрос брокеру = один файл: делить выбор на части сайт не берётся.
    if (n > CUSTOMS_BULK_MAX) {
      toast.error(BROKER_TOO_MANY_TEXT)
      return
    }
    setSending(true)
  }

  const sendToBroker = async ({ brokerId, note }: { brokerId: string | null; note: string }) => {
    setBusy(true)
    try {
      const detail = await exportToBroker({
        itemIds: selected.map((r) => r.itemId),
        brokerId,
        note,
      })
      const to = detail.broker ? ` → ${detail.broker.name}` : ''
      toast.success(
        `Запрос ${detail.fileCode}${to}: ${pluralPositions(detail.positions.length || n)}, партномеров ${detail.itemsCount}. Файл скачан.`,
      )
      setSending(false)
      onDone()
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось отправить позиции брокеру'))
    } finally {
      setBusy(false)
      refresh()
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap rounded-xl border border-gray-200 bg-white px-3 py-2">
      <span className="text-sm text-gray-600 mr-1">
        Выбрано: <span className="font-medium text-gray-900">{n}</span>
        {n > 0 && <span className="text-gray-500"> · партномеров: {uniqueParts}</span>}
      </span>
      <button type="button" className={BTN_PRIMARY} disabled={none} onClick={onAnswer}>
        Ввести ответ
      </button>
      <button
        type="button"
        className={BTN}
        disabled={none || withPrevious.length === 0}
        onClick={() => void keepPrevious()}
        title="Ответ прошлой проверки этого партномера остаётся в силе — без нового запроса брокеру"
      >
        Оставить прошлый ответ
      </button>
      <button type="button" className={BTN} disabled={none} onClick={onNeedsInfo}>
        Нужна информация
      </button>
      <button
        type="button"
        className={BTN}
        disabled={none}
        onClick={() => void setStatus('NOT_REQUIRED')}
      >
        Не требуется
      </button>
      <button
        type="button"
        className={BTN}
        disabled={none}
        onClick={() => void setStatus('PENDING')}
      >
        Вернуть в очередь
      </button>
      <button
        type="button"
        className={BTN}
        disabled={none}
        onClick={openSend}
        title="Excel брокеру: уникальные партномера выбранных позиций"
      >
        Отправить брокеру
      </button>
      <span className="flex-1" />
      <BrokerUploadButton />
      {sending && (
        <SendToBrokerModal
          positions={n}
          uniqueParts={uniqueParts}
          alreadySent={selected.filter((r) => r.status === 'SENT').length}
          busy={busy}
          onClose={() => setSending(false)}
          onSubmit={(params) => void sendToBroker(params)}
        />
      )}
    </div>
  )
}
